import React from 'react';
import {useShallow} from 'zustand/react/shallow';
import {designSystemToken, WizardLayout} from '@lightricks/react-design-system';
import ConsentFormScreen, {
  CONSENT_FORM_VERSION,
} from '@contentlab/components/consent-form/ConsentFormScreen';
import {ROUTES} from '@contentlab/config/routesConstants';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import useSetOnboardingFlags from '@contentlab/hooks/use-set-onboarding-flags/useSetOnboardingFlags';
import useUpdateConsent from '@contentlab/hooks/use-update-consent/useUpdateConstent';
import {ONBOARDING_FLAGS} from '@contentlab/types/onBoardingFlags';
import {FeatureName} from '@contentlab/types/project';
import getEmailDomainType, {
  EmailDomainType,
} from '@contentlab/utils/getEmailDomainType';
import {
  ONBOARDING_STEPS,
  ONBOARDING_STEPS_ORDER,
  OnboardingStep,
  ONBOARDING_STEPS_PRESENTATION_ID,
} from '@contentlab/views/contentlab/pages/onboarding-page/onboardingSteps';
import onboardingWizardStore, {
  onboardingWizardActions,
  OnboardingWizardState,
  USER_TYPES,
} from '@contentlab/views/contentlab/pages/onboarding-page/onboardingWizardStore';
import AnalyticsService, {
  eventNames,
} from '@/services/analytics/AnalyticsService';
import Sentry from '@/services/sentry/Sentry';
import {FLOW_NAMES} from '@/lib/analytics/analyticsConstants';
import {setContentLabOnboardingTargetFeature} from '@/utils/LtidStorage';
import queryClient from '@/utils/reactQueryClient';
import {DashboardUser} from '@/types/dashboardUser';
import useDashboardUserQuery from '@/hooks/queries/use-dashboard-user-query';
import useIsMobile from '@/hooks/use-is-mobile';
import useNavigation from '@/hooks/use-navigation/useNavigation';
import styles from './onboardingWizard.module.scss';
import FeatureSelectionMenu from './steps/FeatureSelection/FeatureSelectionMenu';
import UserTypeSelectionMenu from './steps/UserTypeSelection/UserTypeSelectionMenu';
import getStepData from './utils/getStepData';

type SurveyAnalyticsData = {
  maxOptions: string;
  isReplyRequired: string;
  questionResponse: string;
  response_details: string;
};

const stepToSurveyAnalyticsMap: Record<
  OnboardingStep,
  (
    state: OnboardingWizardState,
    dashboardUser: DashboardUser
  ) => SurveyAnalyticsData
> = {
  [ONBOARDING_STEPS.CONSENT_FORM]: (state) => ({
    maxOptions: '1',
    isReplyRequired: 'true',
    questionResponse: JSON.stringify(state.consentFormAccepted),
    response_details: '',
  }),
  [ONBOARDING_STEPS.USER_TYPE_SELECTION]: (state, dashboardUser) => {
    const emailDomain =
      dashboardUser?.email?.split('@')[1]?.toLowerCase() || '';
    const emailDomainType = getEmailDomainType(emailDomain);
    return {
      maxOptions: '3',
      isReplyRequired: 'true',
      questionResponse: state.selectedUserType || '',
      response_details: JSON.stringify({
        email_domain: emailDomain,
        email_domain_type: emailDomainType,
        is_business:
          (state.selectedUserType || '') !== USER_TYPES.PRIVATE_USER ||
          emailDomainType === EmailDomainType.Business,
      }),
    };
  },
  [ONBOARDING_STEPS.FEATURE_SELECTION]: (state) => ({
    maxOptions: Object.keys(FeatureName).length.toString(),
    isReplyRequired: 'true',
    questionResponse: JSON.stringify(state.selectedFeature),
    response_details: '',
  }),
};

const getSurveyAnalyticsData = (
  state: OnboardingWizardState,
  dashboardUser: DashboardUser
): SurveyAnalyticsData => {
  return stepToSurveyAnalyticsMap[state.step](state, dashboardUser);
};

const sendSurveyAnalytics = (
  stepIndex: number,
  title: string,
  subtitle: string,
  dashboardUser: DashboardUser
) => {
  const state = onboardingWizardStore.getState();
  const flow = AnalyticsService.getOrCreateFlow(
    FLOW_NAMES.CONTENT_STUDIO.ONBOARDING
  );
  const surveyAnalyticsData = getSurveyAnalyticsData(state, dashboardUser);
  const eventPayload = {
    response_id: flow.flow_id,
    question_location: `${stepIndex}`,
    presentation_id: ONBOARDING_STEPS_PRESENTATION_ID[state.step],
    question_id: state.step,
    question_title: title,
    question_subtitle: subtitle,
    is_reply_required: surveyAnalyticsData.isReplyRequired,
    max_options: surveyAnalyticsData.maxOptions,
    question_response: surveyAnalyticsData.questionResponse,
    response_details: surveyAnalyticsData.response_details,
  };

  AnalyticsService.dispatchEvent(
    eventNames.CONTENT_STUDIO_SURVEY_RESPONSE,
    eventPayload
  );
};

const getContentForStep = (
  step: OnboardingStep,
  onAccept: () => void,
  onNext: () => void
): React.ReactNode => {
  const contentByStepMap = {
    [ONBOARDING_STEPS.CONSENT_FORM]: <ConsentFormScreen onAccept={onAccept} />,
    [ONBOARDING_STEPS.USER_TYPE_SELECTION]: (
      <UserTypeSelectionMenu onNext={onNext} />
    ),
    [ONBOARDING_STEPS.FEATURE_SELECTION]: (
      <FeatureSelectionMenu onNext={onNext} />
    ),
  };
  return contentByStepMap[step];
};

const canGoToNextStep = (step: OnboardingStep): boolean => {
  const state = onboardingWizardStore.getState();
  const checkByStepMap = {
    [ONBOARDING_STEPS.CONSENT_FORM]: state.consentFormAccepted,
    [ONBOARDING_STEPS.USER_TYPE_SELECTION]: state.selectedUserType,
    [ONBOARDING_STEPS.FEATURE_SELECTION]: state.selectedFeature,
  };
  return !!checkByStepMap[step];
};

export default function OnboardingWizard() {
  const {selectedFeature, step} = onboardingWizardStore(
    useShallow((state) => ({
      selectedFeature: state.selectedFeature,
      step: state.step,
    }))
  );
  const isMobile = useIsMobile();

  const {data: dashboardUser} = useDashboardUserQuery({});

  const {uiInteraction} = useUiInteractionEvent();
  const {navigate} = useNavigation();
  const {mutateAsync: updateConsent} = useUpdateConsent();
  const setOnboardingCompleted = useSetOnboardingFlags(
    ONBOARDING_FLAGS.HAS_COMPLETED_CURRENT_ONBOARDING_VERSION
  );
  const handleNext = () => {
    const currentStepIndex = ONBOARDING_STEPS_ORDER.indexOf(step);
    uiInteraction({
      item_name: 'next',
      interaction_location: `onboarding_${step}`,
      flow_name: FLOW_NAMES.CONTENT_STUDIO.ONBOARDING,
      interaction_details: JSON.stringify(onboardingWizardStore.getState()),
    });

    sendSurveyAnalytics(
      currentStepIndex,
      title,
      description,
      dashboardUser as DashboardUser
    );

    if (currentStepIndex === ONBOARDING_STEPS_ORDER.length - 1) {
      if (!selectedFeature) {
        return;
      }
      setOnboardingCompleted();
      setContentLabOnboardingTargetFeature(selectedFeature);

      // Invalidate dashboard user cache when transitioning to pricing step, -
      // in order to force refetching the dashboard user.
      // This is a plaster for a bug that we are not sure of yet where
      // after sign up the dashboard user lacks the content lab member role
      // in this stage it should be ok and fetching it again should allow the
      // user to continue to use the content lab.
      queryClient.invalidateQueries(['dashboardUser']);

      navigate(`${ROUTES.CONTENT_LAB}${ROUTES.CONTENT_LAB_PRICING}`);
      return;
    }
    const nextStep = ONBOARDING_STEPS_ORDER[currentStepIndex + 1];
    onboardingWizardActions.setStep(nextStep);
  };

  const {title, description, renderImage} = getStepData(step);

  const onAccept = async () => {
    try {
      await updateConsent(CONSENT_FORM_VERSION);
    } catch (error) {
      Sentry.captureException(error as object, {
        title: 'Content studio consent update error',
        error,
      });
    }
    onboardingWizardActions.setConsentFormAccepted(true);
    handleNext();
  };

  return (
    <div
      className={`${styles.onboardingWizard} ${
        step === ONBOARDING_STEPS.CONSENT_FORM ? styles.consentForm : ''
      }`}
    >
      <WizardLayout
        heading={title}
        subheading={description}
        progress={null}
        progressBarColor={designSystemToken('semantic.bg.neutral')}
        renderContent={() => getContentForStep(step, onAccept, handleNext)}
        onNext={handleNext}
        defaultFooterButtonProps={{hideNextButton: true}}
        isNextDisabled={!canGoToNextStep(step)}
        renderDesktopVisual={isMobile ? undefined : renderImage}
      />
    </div>
  );
}
